import { useApolloClient } from '@apollo/client';
import { faArrowRotateBack, faPen, faSave } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CircularProgress, Divider, List, ListItem, ListItemText } from '@material-ui/core';
import { useFormik } from 'formik';
import Fuse from 'fuse.js';
import React, { SetStateAction, useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { ContentTaskGq } from '../../../../../graphql/projects/models/ContentTaskGq';
import { ContentTaskSortingTypeGq } from '../../../../../graphql/projects/models/ContentTaskSortingTypeGq';
import { projectsApiService } from '../../../../../graphql/projects/projectsApiService';
import {
  DataGq,
  gqTag,
  VariablesGq,
} from '../../../../../graphql/projects/queries/contentPlan/useGetContentTasksGq';
import { getTaskUrl } from '../../Tables/ContentTaskTable/TaskUrl';
import { ProjectTabChoices } from '../../../../ProjectPage/ProjectPage';
import VoxelIconButton from '../../../../../components/voxel/Buttons/VoxelIconButton';
import VoxelDialog from '../../../../../components/voxel/Dialogs/VoxelDialog';
import VoxelInput from '../../../../../components/voxel/Forms/VoxelInput';
import VoxelLink from '../../../../../components/voxel/Link/VoxelLink';
import VoxelText from '../../../../../components/voxel/Text/VoxelText';
import VoxelDialogContent from '../../../../../components/voxel/Dialogs/VoxelDialogContent';
import VoxelDialogActionCancel from '../../../../../components/voxel/Dialogs/VoxelDialogActionCancel';
import VoxelDialogActionSubmit from '../../../../../components/voxel/Dialogs/VoxelDialogActionSubmit';
import VoxelDialogActions from '../../../../../components/voxel/Dialogs/VoxelDialogActions';

export interface ContentTaskUrlProps {
  contentTask: ContentTaskGq;
  clientUrl?: string;
  reload: () => Promise<any>;
  setEditUrl: React.Dispatch<SetStateAction<boolean>>;
  editUrl: boolean;
}

interface FormState {
  url: string;
  ignoreWarning: boolean;
  warningUrls: ContentTaskGq[];
  exactWarningUrl?: ContentTaskGq;
}

export const ContentTaskUrl: React.FC<ContentTaskUrlProps> = ({
  contentTask,
  clientUrl,
  reload,
  editUrl,
  setEditUrl,
}) => {
  // Get existing urls
  // Data fetching
  const apolloClient = useApolloClient();
  const getExistingUrls = useCallback(
    (url: string) =>
      apolloClient.query<DataGq, VariablesGq>({
        query: gqTag,
        variables: {
          command: {
            rowsPerPage: 100,
            page: 0,
            filtering: {
              urlSearchTerm: url,
              projectId: contentTask.projectId,
              approvedStatus: [],
              actions: [],
              tagFilters: [],
              keywordFilters: [],
              statusCodes: [],
            },
            sorting: {
              type: ContentTaskSortingTypeGq.URL,
            },
          },
        },
      }),
    [contentTask.projectId]
  );

  // Form logic
  const updateMutation = projectsApiService.mutations.contentPlan.useUpdateTaskDetails();

  const taskUrl = useMemo(
    () => getTaskUrl(contentTask.url, clientUrl),
    [clientUrl, contentTask.url]
  );

  const formBag = useFormik<FormState>({
    initialValues: {
      url: taskUrl ?? '',
      ignoreWarning: false,
      warningUrls: [],
    },
    enableReinitialize: true,
    onSubmit: async (values, actions) => {
      const cleanedUrl = values.url.startsWith('/') ? values.url.substring(1) : values.url;
      let cleanedClient = '';
      if (!clientUrl?.length) {
        cleanedClient = '/';
      } else if (clientUrl.endsWith('/')) {
        cleanedClient = clientUrl;
      } else {
        cleanedClient = clientUrl + '/';
      }
      const completeUrl = cleanedClient + cleanedUrl;

      if (!values.ignoreWarning) {
        // See if suggestion matches similar URLs
        const existingUrls = await getExistingUrls(cleanedUrl);
        const relevantExistingUrls =
          existingUrls.data.projects.getContentTasks.contentTasks.filter(
            (ct) => ct.id !== contentTask.id
          ) ?? [];
        const fuse = new Fuse(relevantExistingUrls, {
          keys: [
            {
              getFn: (c) => c.url,
              name: 'url',
            },
          ],
          includeScore: true,
        });
        const fuzzyResults = fuse.search({ url: completeUrl });
        const exactWarningUrl = fuzzyResults.find((f) => f.item.url === completeUrl)?.item;
        if (fuzzyResults.length) {
          actions.setValues({
            ...values,
            warningUrls: fuzzyResults.map((v) => v.item),
            exactWarningUrl: exactWarningUrl,
          });
          return;
        }
      }

      await updateMutation({
        taskId: contentTask.id,
        urlNew: completeUrl,
        projectId: contentTask.projectId,
      })
        .then(reload)
        .then(() => {
          // Wait a bit with resetting everything - for new URL values to propagate
          setTimeout(() => {
            actions.setSubmitting(false);
          }, 200);
          return Promise.resolve();
        });
    },
  });

  // Moving
  const moveMutation = projectsApiService.mutations.contentPlan.useAddTaskKeywords();
  const history = useHistory();
  const [updating, setUpdating] = useState(false);
  const handleMove = (destinationTaskId: number) => {
    setUpdating(true);
    moveMutation({
      projectId: contentTask.projectId,
      destinationContentTaskId: destinationTaskId,
      sourceContentKeywordIds: contentTask.keywords.map((kw) => kw.id),
      destinationOrderKeywordIds: [],
      insertByVolumeOrder: true,
    })
      .then(() => reload())
      .then(() => {
        history.push(`/${contentTask.projectId}/${ProjectTabChoices.Plan}/${destinationTaskId}`);
      })
      .finally(() => {
        setUpdating(false);
      });
  };
  const handleClose = () =>
    formBag.setValues({ url: formBag.values.url, ignoreWarning: false, warningUrls: [] });

  return (
    <div className="w-full">
      {editUrl ? (
        <form
          className="w-full flex flex-row flex-nowrap items-center justify-between gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          <div className="grow">
            <VoxelInput
              value={formBag.values.url}
              inputWidthClass="w-full"
              placeholder={'Url'}
              autoFocus={true}
              disabled={formBag.isSubmitting}
              onChange={(event) =>
                formBag.setValues({
                  url: event.target.value,
                  ignoreWarning: false,
                  warningUrls: [],
                  exactWarningUrl: undefined,
                })
              }
            />
          </div>
          <div className="flex flex-row items-center flex-nowrap">
            <VoxelIconButton
              tooltip="Save url"
              type={'submit'}
              disabled={formBag.isSubmitting}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                formBag.submitForm();
                setEditUrl(false);
              }}
              size={'sm'}
              color={'white'}
              icon={<FontAwesomeIcon icon={faSave} size="lg" />}
            />
            <VoxelIconButton
              tooltip="Revert changes"
              disabled={formBag.isSubmitting || formBag.values.url === formBag.initialValues.url}
              onClick={() => formBag.resetForm()}
              size={'sm'}
              color={'white'}
              icon={<FontAwesomeIcon icon={faArrowRotateBack} size="lg" />}
            />
          </div>
        </form>
      ) : (
        <div className="basis-2/3 ml-1 w-full flex flex-row items-center gap-2 group">
          <VoxelText variant="title2" colorClassName="text-voxel-blue" ellipsis>
            <VoxelLink
              href={contentTask.url}
              openInNewTab
              onMouseDown={(event: React.MouseEvent<any>) => {
                event.stopPropagation();
              }}
            >
              {getTaskUrl(contentTask.url, clientUrl)}
            </VoxelLink>
          </VoxelText>
          <VoxelIconButton
            tooltip="Edit url"
            icon={
              <FontAwesomeIcon
                icon={faPen}
                className="text-voxel-gray-500 hidden group-hover:block"
              />
            }
            color="white"
            onClick={() => setEditUrl(true)}
          />
        </div>
      )}
      {/*  Warning dialog if the URL is similar to an existing URL */}
      <VoxelDialog
        open={formBag.values.warningUrls.length && !formBag.values.ignoreWarning}
        onClose={handleClose}
        title={'Similar URLs found in plan'}
        subtitle={'Choose a URL to move keywords instead'}
      >
        <VoxelDialogContent>
          <div className="w-full">
            <List>
              {formBag.values.warningUrls.map((warningUrl, index) => (
                <React.Fragment key={warningUrl.id}>
                  {index > 0 && <Divider />}
                  <ListItem
                    key={warningUrl.id}
                    button={updating ? undefined : true}
                    onClick={updating ? undefined : () => handleMove(warningUrl.id)}
                    dense
                  >
                    <ListItemText
                      primary={warningUrl.url}
                      secondary={`${warningUrl.keywords.length} keywords`}
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </div>
        </VoxelDialogContent>
        <VoxelDialogActions>
          <VoxelDialogActionCancel onClick={handleClose} />
          <VoxelDialogActionSubmit
            endIcon={updating ? <CircularProgress size={18} /> : undefined}
            title={updating ? 'Processing' : 'Continue updating this URL'}
            onClick={() => {
              formBag.setFieldValue('ignoreWarning', true);
              formBag.handleSubmit();
            }}
            disabled={!!formBag.values.exactWarningUrl}
          />
        </VoxelDialogActions>
      </VoxelDialog>
    </div>
  );
};

export default ContentTaskUrl;
